import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import customerSettingsApi from "./api/customerSettingsApi";
import { ChangeBusinessParameters, ChangeGeneralParameters, ChangeOfficeParameters, ChangePortfolioParameters, ChangeSocialParameters, CustomerSettingsState } from "./models/models";
import customerManufacturersApi from "../shared/api/customerManufacturersApi";
import customerCategoriesApi from "../shared/api/customerCategoriesApi";
import { FileModel } from "../../shared/models/models";

export const getCustomer = createAsyncThunk(
  "customerSettings/getCustomer",
  async () => {
    return customerSettingsApi.getCustomer();
  }
);

export const editLogoSocials = createAsyncThunk(
  "customerSettings/editLogoSocials",
  async (model: ChangeSocialParameters) => {
    return customerSettingsApi.changeLogoSocials(model);
  }
);

export const editGeneralDetails = createAsyncThunk(
  "customerSettings/editGeneralDetails",
  async (model: ChangeGeneralParameters) => {
    return customerSettingsApi.changeGeneralDetails(model);
  }
);

export const editOffice = createAsyncThunk(
  "customerSettings/editOffice",
  async (model: ChangeOfficeParameters) => {
    return customerSettingsApi.changeOffice(model);
  }
);

export const editDescription = createAsyncThunk(
  "customerSettings/editDescription",
  async (description: string) => {
    return customerSettingsApi.changeDescription(description);
  }
);

export const editPortfolio = createAsyncThunk(
  "customerSettings/editPortfolio",
  async (model: ChangePortfolioParameters) => {
    return customerSettingsApi.changePortfolio(model);
  }
);

export const editBusiness = createAsyncThunk(
  "customerSettings/editBusiness",
  async (model: ChangeBusinessParameters) => {
    return customerSettingsApi.changeBusiness(model);
  }
);

export const editBanner = createAsyncThunk(
  "customerSettings/editBanner",
  async (banner: FileModel | null) => {
    return customerSettingsApi.changeBanner(banner);
  }
);

export const getManufacturers = createAsyncThunk(
  "customerMyRfqs/getManufacturers",
  async () => {
    return customerManufacturersApi.getManufacturers();
  }
);

export const getCategories = createAsyncThunk(
  "customerMyRfqs/getCategories",
  async () => {
    return customerCategoriesApi.getCategories();
  }
);

const initialState: CustomerSettingsState = {
  customer: undefined,
  manufacturers: [],
  categories: [],
  loading: false,
  success: undefined,
  error: undefined
};

const customerSettingsSlice = createSlice({
  name: "customerSettings",
  initialState: initialState,
  reducers: {
    resetCustomer: (state) => {
      state.customer = undefined;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomer.fulfilled, (state, { payload }) => {
        state.customer = payload;
        state.loading = false;
      })
      .addCase(getCustomer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editGeneralDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(editGeneralDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editGeneralDetails.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editLogoSocials.pending, (state) => {
        state.loading = true;
      })
      .addCase(editLogoSocials.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editLogoSocials.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editOffice.pending, (state) => {
        state.loading = true;
      })
      .addCase(editOffice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editOffice.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editDescription.pending, (state) => {
        state.loading = true;
      })
      .addCase(editDescription.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editDescription.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editPortfolio.pending, (state) => {
        state.loading = true;
      })
      .addCase(editPortfolio.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editPortfolio.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editBusiness.pending, (state) => {
        state.loading = true;
      })
      .addCase(editBusiness.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editBusiness.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(editBanner.pending, (state) => {
        state.loading = true;
      })
      .addCase(editBanner.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editBanner.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
      });

    builder
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
      });
  }
});

export const {
  resetCustomer,
  setSuccess,
  setLoading,
  setError
} = customerSettingsSlice.actions;

export default customerSettingsSlice.reducer;